import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Popover,
    TextField,
    Typography
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

const normalize = (value) => (value ?? '').toString();

const ExcelColumnFilter = ({
    label,
    options = [],
    selectedOptions,
    onChange,
    emptyLabel = '(Vacío)'
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');

    const normalizedOptions = useMemo(() => {
        const seen = new Set();
        const list = [];
        options.forEach((value) => {
            const normalized = normalize(value);
            if (!seen.has(normalized)) {
                seen.add(normalized);
                list.push(normalized);
            }
        });
        return list.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [options]);

    const activeSelected = selectedOptions === undefined ? normalizedOptions : selectedOptions;
    const selectedSet = useMemo(() => new Set((activeSelected || []).map(normalize)), [activeSelected]);

    const visibleOptions = useMemo(() => {
        const filterText = search.trim().toLowerCase();
        if (!filterText) {
            return normalizedOptions;
        }
        return normalizedOptions.filter((value) => {
            const displayValue = value === '' ? emptyLabel : value;
            return displayValue.toLowerCase().includes(filterText);
        });
    }, [normalizedOptions, search, emptyLabel]);

    const isFiltered = selectedOptions !== undefined && selectedOptions.length !== normalizedOptions.length;

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearch('');
    };

    const handleToggleOption = (value) => {
        const normalizedValue = normalize(value);
        const next = new Set(selectedSet);
        if (next.has(normalizedValue)) {
            next.delete(normalizedValue);
        } else {
            next.add(normalizedValue);
        }
        const nextArray = Array.from(next);
        if (nextArray.length === normalizedOptions.length) {
            onChange(undefined);
            return;
        }
        onChange(nextArray);
    };

    const handleSelectAll = () => {
        onChange(undefined);
    };

    const handleClearAll = () => {
        onChange([]);
    };

    const open = Boolean(anchorEl);

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="inherit">{label}</Typography>
            <IconButton
                size="small"
                onClick={handleOpen}
                color={isFiltered ? 'primary' : 'default'}
                aria-label={`Filtrar ${label}`}
            >
                <FilterListIcon fontSize="small" />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 1.5, width: 280 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Filtrar {label}
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Buscar valor"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button size="small" onClick={handleSelectAll}>Todos</Button>
                        <Button size="small" onClick={handleClearAll}>Ninguno</Button>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                        {visibleOptions.map((value) => {
                            const displayValue = value === '' ? emptyLabel : value;
                            return (
                                <FormControlLabel
                                    key={`opt-${displayValue}`}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={selectedSet.has(value)}
                                            onChange={() => handleToggleOption(value)}
                                        />
                                    )}
                                    label={displayValue}
                                    sx={{ display: 'flex', mr: 0 }}
                                />
                            );
                        })}
                        {visibleOptions.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Sin coincidencias.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default ExcelColumnFilter;
